import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { isDatadogRUMEnabled, isDatadogLogsEnabled } from '@/lib/datadog'

/**
 * Posts any error to Datadog RUM. Safe to call when RUM is not configured (no-op).
 * Use in: ErrorBoundary.onError, useErrorHandler, window.unhandledrejection.
 */
export function reportToDatadog(error: unknown, context?: Record<string, unknown>): void {
	if (!isDatadogRUMEnabled()) return
	const err = error instanceof Error ? error : new Error(String(error))
	datadogRum.addError(err, context)
}

/**
 * Logs a recoverable warning to Datadog Logs (not RUM).
 * Use in catch blocks where the app continues after the failure.
 * All catch blocks must go through here — never import datadogLogs directly.
 */
export function logWarning(message: string, context?: Record<string, unknown>): void {
	if (!isDatadogLogsEnabled()) return
	datadogLogs.logger.warn(message, context)
}

/**
 * Logs an error-level entry to Datadog Logs (not RUM).
 * Use for errors that need log-level tracking (e.g. QueryCache/MutationCache failures).
 * For RUM error tracking use reportToDatadog instead.
 */
export function logError(message: string, context?: Record<string, unknown>): void {
	if (!isDatadogLogsEnabled()) return
	datadogLogs.logger.error(message, context)
}
